import { createFileRoute } from '@tanstack/react-router';
import { use } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, ClauseScannerAnalysisType } from '@/app/utils/constants';
import { ContractsIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { AppContext } from '@/app/appContext';
import { useGetClauseScannerSubcategoryQuery } from '@/app/hooks/useClauseScanner';
import { ClausesSubCategoryPageContent } from '@/app/components/features/evaluations/Clauses/ClausesSubCategoryPageContent';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(main)/clauses/$subcategoryId/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractHome');
  const tFeature = useTranslations('features.clauses');
  const { contractId, projectId, subcategoryId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { userMeta } = use(AppContext);
  const organizationId = userMeta?.organizationId || '';

  const { data } = useGetClauseScannerSubcategoryQuery({
    subCategoryId: subcategoryId,
    resourceId: contractId,
    type: ClauseScannerAnalysisType.CONTRACT,
    enabled: !!organizationId,
  });

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/contracts/$contractId/clauses"
          params={{ projectId, contractId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={ContractsIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId/clauses"
                params={{ projectId, contractId }}
              >
                {tFeature('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{data?.name || '...'}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <ClausesSubCategoryPageContent
        evaluationId={contractId}
        type={ClauseScannerAnalysisType.CONTRACT}
      />
    </>
  );
}
